// client/src/components/pages/LexiStar/RecomputeJobs/RecomputeOffer.jsx
//
// "Apply going forward" / "Recompute history…" (LS-09 §5.19; T4-S12). Saving
// a geofence, corridor, threshold or norm changes only what the engine does
// next; history keeps the numbers computed under the old configuration until
// someone asks for a recompute and a second user publishes its diff.
//
// Mounted with ONE line by the editors that save versioned configuration
// (contract §9): it watches the objects it is given and offers a recompute when
// one of them comes back with a higher version — no callback wiring needed in
// the editor.
//
//   <RecomputeOffer objectType="geofence" objects={geofences} idKey="GeofenceID" versionKey="CurrentVersion" nameKey="Name" />
//   <RecomputeOffer objectType="route" objects={route ? [route] : []} />
//
// The dialog (also used for manual recomputes on the Recompute jobs page)
// picks the scope (all trucks or chosen trucks; for a route, its trips) and
// the time range, queues POST /recompute-jobs and shows the server's estimate
// of what will be re-read. Nothing changes until the diff is published.
// Renders nothing without lexistar.recompute.run.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaHistory, FaCheck } from 'react-icons/fa';
import { useAuth } from '../../../../context/AuthContext';
import ThemedSelect from '../../../common/ThemedSelect';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import { listAssets } from '../../../../services/lexistarAssets';
import { createRecomputeJob, pickObject } from '../../../../services/lexistarAssurance';
import { fmtNum } from '../StatusBoard/format';
import { Modal, Field, InlineError, buttonClasses } from '../ReviewQueue/assuranceAtoms';
import { errorMessage, localToIso, toLocalValue } from '../ReviewQueue/assuranceShared';
import { triggerLabel } from './recomputeLogic';

const DAY_MS = 86400000;
const DEFAULT_DAYS = 7;
/** LS-04 §13: recompute is bounded by the 24-month full-resolution window. */
const MAX_HISTORY_DAYS = 730;

const assetKeyOf = (a) =>
  a.asset_key ||
  (a.registration_normalised || a.registration
    ? `reg:${a.registration_normalised || String(a.registration).replace(/\s+/g, '').toUpperCase()}`
    : null);

/** Trucks that carry telemetry (trailers never have their own positions). */
function useTruckOptions(open) {
  const [assets, setAssets] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    listAssets({ limit: 500 })
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res?.assets) ? res.assets : [];
        setAssets(
          list
            .filter((a) => a.asset_type !== 'trailer')
            .map((a) => ({
              key: assetKeyOf(a),
              label: a.fleet_no ? `${a.registration} (${a.fleet_no})` : a.registration,
            }))
            .filter((a) => a.key)
        );
      })
      .catch(() => alive && setAssets([]));
    return () => {
      alive = false;
    };
  }, [open]);
  return assets;
}

/**
 * @param {{open:boolean, trigger:{object_type:string, object_id?:string|number, version?:number, name?:string},
 *          routeIds?:string[], onClose:()=>void, onCreated?:(job)=>void}} props
 */
export function RecomputeDialog({ open, trigger, routeIds = [], onClose, onCreated }) {
  const [scopeMode, setScopeMode] = useState(routeIds.length ? 'route' : 'all');
  const [assetKeys, setAssetKeys] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const trucks = useTruckOptions(open);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setScopeMode(routeIds.length ? 'route' : 'all');
    setAssetKeys([]);
    setStart(toLocalValue(new Date(now.getTime() - DEFAULT_DAYS * DAY_MS)));
    setEnd(toLocalValue(now));
    setError(null);
    setJob(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fromIso = localToIso(start);
  const toIso = localToIso(end);
  const days = fromIso && toIso ? (new Date(toIso) - new Date(fromIso)) / DAY_MS : null;

  const submit = async () => {
    if (!fromIso || !toIso) return setError('Choose the period to recompute.');
    if (new Date(toIso) <= new Date(fromIso)) return setError('The end must be after the start.');
    if (new Date(toIso) > new Date(Date.now() + 60000))
      return setError('History ends now — the future is not recomputed.');
    if (days > MAX_HISTORY_DAYS)
      return setError('Recompute reaches back at most 24 months (the full-resolution window).');
    if (scopeMode === 'assets' && assetKeys.length === 0)
      return setError('Choose at least one truck, or recompute all trucks.');
    setBusy(true);
    setError(null);
    const scope = {
      asset_keys: scopeMode === 'assets' ? assetKeys : [],
      route_ids: scopeMode === 'route' ? routeIds : [],
      trip_ids: [],
      from: fromIso,
      to: toIso,
    };
    try {
      const res = await createRecomputeJob({
        trigger: {
          object_type: trigger?.object_type || 'manual',
          object_id: trigger?.object_id != null ? String(trigger.object_id) : undefined,
          version: trigger?.version ?? undefined,
        },
        scope,
        range: { from: fromIso, to: toIso },
      });
      const created = pickObject(res, 'job', 'recompute_job');
      setJob(created);
      onCreated?.(created);
    } catch (err) {
      setError(errorMessage(err, 'The recompute was not queued.'));
    } finally {
      setBusy(false);
    }
  };

  const estimate = job?.estimate || {};
  return (
    <Modal
      open={open}
      title={job ? 'Recompute queued' : 'Recompute history'}
      onClose={onClose}
      footer={
        job ? (
          <>
            <button type="button" onClick={onClose} className={buttonClasses.secondary}>
              Close
            </button>
            {job.id && (
              <Link
                to={`/lexistar/recompute-jobs/${job.id}`}
                className={buttonClasses.primary}
                onClick={onClose}
              >
                Follow the job
              </Link>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} className={buttonClasses.secondary}>
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className={buttonClasses.primary}
            >
              {busy ? 'Queuing…' : 'Queue recompute'}
            </button>
          </>
        )
      }
    >
      {job ? (
        <div className="space-y-3" role="status">
          <p className="text-sm text-slate-700">
            The engine will replay the stored positions for this period into a shadow copy and build
            a before/after diff. <strong>Nothing changes</strong> until a second authorised user
            publishes it.
          </p>
          <dl className="grid grid-cols-3 gap-3" aria-label="Estimate">
            {[
              ['Positions', estimate.positions],
              ['Trips', estimate.trips],
              ['Events', estimate.events],
            ].map(([label, v]) => (
              <div key={label} className="rounded-lg border border-slate-200 px-3 py-2">
                <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
                <dd className="text-lg font-bold text-slate-900 tabular-nums">
                  {v != null ? fmtNum(v) : '—'}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-xs text-slate-500">Estimated records the recompute will re-read.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{triggerLabel(trigger)}</span>
            {trigger?.object_type === 'manual'
              ? ' — rebuild derived records from the stored positions with the current configuration.'
              : ' — see what history would look like under this version.'}
          </p>
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700 mb-1.5">Scope</legend>
            <div className="space-y-1.5 text-sm text-slate-700">
              {routeIds.length > 0 && (
                <label className="flex items-center gap-2 min-h-[36px]">
                  <input
                    type="radio"
                    name="recompute-scope"
                    checked={scopeMode === 'route'}
                    onChange={() => setScopeMode('route')}
                  />
                  Trips on this route
                </label>
              )}
              <label className="flex items-center gap-2 min-h-[36px]">
                <input
                  type="radio"
                  name="recompute-scope"
                  checked={scopeMode === 'all'}
                  onChange={() => setScopeMode('all')}
                />
                All trucks
              </label>
              <label className="flex items-center gap-2 min-h-[36px]">
                <input
                  type="radio"
                  name="recompute-scope"
                  checked={scopeMode === 'assets'}
                  onChange={() => setScopeMode('assets')}
                />
                Chosen trucks
              </label>
            </div>
            {scopeMode === 'assets' && (
              <div className="mt-2">
                <ThemedSelect
                  isMulti
                  value={assetKeys}
                  onChange={(e) => setAssetKeys(e.target.value)}
                  aria-label="Trucks"
                  placeholder="Choose trucks…"
                >
                  {trucks.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
            )}
          </fieldset>
          <Field label="Period" hint="Up to 24 months back; ends at the present at the latest.">
            <UnifiedDateTimeRangePicker
              startValue={start}
              endValue={end}
              onStartChange={setStart}
              onEndChange={setEnd}
              placeholder="Choose the period"
            />
          </Field>
          {days != null && days > 0 && (
            <p className="text-xs text-slate-500 tabular-nums">
              {fmtNum(days, days < 2 ? 1 : 0)} day{days === 1 ? '' : 's'} of history. The server
              estimates the positions, trips and events to re-read when it queues the job.
            </p>
          )}
          <InlineError message={error} />
        </>
      )}
    </Modal>
  );
}

/**
 * Watches `objects` for a version bump and offers a recompute of history.
 * @param {{objectType:'geofence'|'route'|'norm'|'threshold', objects:Object[], idKey?:string,
 *          versionKey?:string, nameKey?:string}} props
 */
export default function RecomputeOffer({
  objectType,
  objects,
  idKey = 'id',
  versionKey = 'current_version',
  nameKey = 'name',
}) {
  const { hasPermission } = useAuth();
  const canRun = hasPermission('lexistar.recompute.run');
  const seen = useRef(new Map());
  const [offer, setOffer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [done, setDone] = useState(null);

  const snapshot = useMemo(
    () =>
      (Array.isArray(objects) ? objects : [])
        .filter((o) => o && o[idKey] != null && o[versionKey] != null)
        .map((o) => ({ id: String(o[idKey]), version: Number(o[versionKey]), name: o[nameKey] })),
    [objects, idKey, versionKey, nameKey]
  );

  useEffect(() => {
    let bumped = null;
    snapshot.forEach((o) => {
      const prev = seen.current.get(o.id);
      if (prev != null && Number.isFinite(o.version) && o.version > prev) bumped = o;
      seen.current.set(o.id, o.version);
    });
    if (bumped) {
      setOffer(bumped);
      setDone(null);
    }
  }, [snapshot]);

  if (!canRun || !offer) return null;

  const trigger = {
    object_type: objectType,
    object_id: offer.id,
    version: offer.version,
    name: offer.name,
  };
  const what = objectType === 'geofence' ? 'zone' : objectType;

  return (
    <div
      role="status"
      aria-label="Recompute offer"
      className="my-3 rounded-lg border border-slate-300 bg-surface px-4 py-3 text-sm text-slate-800"
    >
      {done ? (
        <p className="flex items-center gap-2">
          <FaCheck size={11} className="text-slate-500" aria-hidden="true" />
          {done === 'forward'
            ? 'Applies going forward only; history keeps its current numbers.'
            : 'Recompute queued — nothing changes until a second user publishes the diff.'}
          <button
            type="button"
            onClick={() => setOffer(null)}
            className="ml-auto text-xs text-slate-500 hover:underline"
          >
            Close
          </button>
        </p>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="flex-1">
            <FaHistory
              size={11}
              className="inline mr-1.5 -mt-0.5 text-slate-500"
              aria-hidden="true"
            />
            Saved {what} {offer.name ? <strong>{offer.name}</strong> : null} as version{' '}
            <span className="tabular-nums">{offer.version}</span>. Past trips and events keep the
            numbers from the previous version unless you recompute them.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDone('forward')}
              className={buttonClasses.secondary}
            >
              Apply going forward
            </button>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className={buttonClasses.primary}
            >
              Recompute history…
            </button>
          </div>
        </div>
      )}
      <RecomputeDialog
        open={dialogOpen}
        trigger={trigger}
        routeIds={objectType === 'route' ? [offer.id] : []}
        onClose={() => setDialogOpen(false)}
        onCreated={() => setDone('queued')}
      />
    </div>
  );
}
